'use client'

import { useCallback, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { useTranslations } from 'next-intl'

import { DEPT_IDS, PERKS, ROLES, type DeptId, type PerkIcon, type Role } from './company-data'
import { SkillCloudCard } from './skill-cloud-card'

const JOBS_LIST_HASH = '#jobs'
const JOBS_DETAIL_HASH_PREFIX = '#jobs/'
/** Matches digen-company-pages `window.scrollTo(... - 72)` under the sticky header. */
const OPEN_ROLES_OFFSET = 72

function readRoleFromHash(): Role | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  if (!hash.startsWith(JOBS_DETAIL_HASH_PREFIX)) return null
  const id = decodeURIComponent(hash.slice(JOBS_DETAIL_HASH_PREFIX.length))
  return ROLES.find(role => role.id === id) ?? null
}

const jobHashListeners = new Set<() => void>()

function subscribeJobHash(onStoreChange: () => void) {
  jobHashListeners.add(onStoreChange)
  window.addEventListener('hashchange', onStoreChange)
  return () => {
    jobHashListeners.delete(onStoreChange)
    window.removeEventListener('hashchange', onStoreChange)
  }
}

function getJobHashSnapshot() {
  return readRoleFromHash()?.id ?? ''
}

function getJobHashServerSnapshot() {
  return ''
}

function replaceJobsHash(jobId?: string) {
  const next = jobId ? `${JOBS_DETAIL_HASH_PREFIX}${jobId}` : JOBS_LIST_HASH
  history.replaceState(null, '', next)
  jobHashListeners.forEach(listener => listener())
}

function forEachCareersScroller(fn: (el: HTMLElement) => void) {
  const seen = new Set<HTMLElement>()
  const marked = document.querySelector<HTMLElement>('[data-app-scroll-container]')
  const main = document.querySelector<HTMLElement>('main.overflow-auto')
  for (const el of [marked, main]) {
    if (!el || seen.has(el)) continue
    seen.add(el)
    fn(el)
  }
}

function resetCareersScroll() {
  forEachCareersScroller(el => {
    el.scrollTop = 0
  })
}

function scrollCareersToElement(
  target: HTMLElement,
  offset: number,
  behavior: ScrollBehavior
) {
  forEachCareersScroller(el => {
    const top =
      target.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop
    el.scrollTo({ top: Math.max(0, top - offset), behavior })
  })
}

export function CareersPageContent() {
  const t = useTranslations('company')
  const [filter, setFilter] = useState<'all' | DeptId>('all')
  const selectedJobId = useSyncExternalStore(
    subscribeJobHash,
    getJobHashSnapshot,
    getJobHashServerSnapshot
  )
  const selectedJob = useMemo(
    () => ROLES.find(role => role.id === selectedJobId) ?? null,
    [selectedJobId]
  )
  const openRolesRef = useRef<HTMLDivElement>(null)
  const pendingScrollToRolesRef = useRef(false)

  const departments = useMemo(() => ['all' as const, ...DEPT_IDS], [])

  const filteredRoles = useMemo(
    () => (filter === 'all' ? ROLES : ROLES.filter(r => r.deptId === filter)),
    [filter]
  )

  const groupedRoles = useMemo(() => {
    const groups: { deptId: DeptId; items: Role[] }[] = []
    filteredRoles.forEach(r => {
      const last = groups[groups.length - 1]
      if (!last || last.deptId !== r.deptId) {
        groups.push({ deptId: r.deptId, items: [r] })
      } else {
        last.items.push(r)
      }
    })
    return groups
  }, [filteredRoles])

  const scrollToRoles = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const target = openRolesRef.current
    if (!target) return
    scrollCareersToElement(target, OPEN_ROLES_OFFSET, behavior)
  }, [])

  const openJob = useCallback((role: Role) => {
    pendingScrollToRolesRef.current = false
    replaceJobsHash(role.id)
  }, [])

  const closeJob = useCallback(() => {
    pendingScrollToRolesRef.current = true
    replaceJobsHash()
  }, [])

  useLayoutEffect(() => {
    if (selectedJob) {
      resetCareersScroll()
      return
    }
    if (!pendingScrollToRolesRef.current) return
    pendingScrollToRolesRef.current = false
    scrollToRoles('auto')
  }, [selectedJob, scrollToRoles])

  if (selectedJob) {
    return <JobDetail role={selectedJob} onBack={closeJob} />
  }

  return (
    <section className="page">
      <header className="jobs-hero">
        <p className="hero-kicker">
          <i />
          <span>{t('careers.kicker')}</span>
          <i />
        </p>
        <h1>{t('careers.title')}</h1>
        <p className="lead">{t('careers.lead')}</p>
        <button className="cta-md" type="button" onClick={() => scrollToRoles()}>
          <span>{t('careers.seeJobs')}</span>
          <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </button>
      </header>

      <section className="jobs-sec">
        <div className="jobs-sec__head">
          <h2>{t('careers.whyTitle')}</h2>
        </div>
        <div className="why-grid">
          <div className="why-copy">
            <p>{t('careers.why1')}</p>
            <p>{t('careers.why2')}</p>
          </div>
          <SkillCloudCard />
        </div>
      </section>

      <section className="jobs-sec perk-band">
        <div className="jobs-sec__head">
          <h2>{t('careers.perkTitle')}</h2>
          <p>{t('careers.perkLead')}</p>
        </div>
        <div className="perks">
          {PERKS.map(perk => (
            <PerkCard key={perk.icon} perk={perk} />
          ))}
        </div>
      </section>

      <section ref={openRolesRef} className="jobs-sec" id="open-roles">
        <div className="jobs-sec__head">
          <h2>{t('careers.rolesTitle')}</h2>
          <p>{t('careers.rolesLead')}</p>
        </div>
        <div className="ashby">
          <div className="ashby-filters">
            {departments.map(dept => (
              <button
                key={dept}
                className={filter === dept ? 'is-on' : undefined}
                type="button"
                onClick={() => setFilter(dept)}
              >
                {dept === 'all' ? t('careers.allDepartments') : t(`careers.depts.${dept}`)}
              </button>
            ))}
          </div>
          <div>
            {groupedRoles.map(group => (
              <div key={group.deptId}>
                <p className="job-dept">{t(`careers.depts.${group.deptId}`)}</p>
                {group.items.map(role => (
                  <button
                    key={role.id}
                    className="role"
                    type="button"
                    onClick={() => openJob(role)}
                  >
                    <b>{t(`careers.jobs.${role.id}.title`)}</b>
                    <span>{t('careers.remote')}</span>
                    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </section>
  )
}

function JobDetail({ role, onBack }: { role: Role; onBack: () => void }) {
  const t = useTranslations('company')
  const title = t(`careers.jobs.${role.id}.title`)
  const duties = t.raw(`careers.jobs.${role.id}.duties`) as string[]
  const reqs = t.raw(`careers.jobs.${role.id}.reqs`) as string[]
  const nice = t.raw(`careers.jobs.${role.id}.nice`) as string[]
  const mailHref = `mailto:team@digen.ai?subject=${encodeURIComponent(
    t('careers.applySubject', { title })
  )}`

  return (
    <article className="jd">
      <button className="jd-back" type="button" onClick={onBack}>
        {t('careers.backToRoles')}
      </button>
      <p className="job-dept">{t(`careers.depts.${role.deptId}`)}</p>
      <h1>{title}</h1>
      <p className="jd-meta">
        {t('careers.remote')} · {t('careers.fullTime')}
      </p>
      <a className="cta-md" href={mailHref}>
        {t('careers.apply')}
      </a>
      <h2>{t('careers.aboutRole')}</h2>
      <p>{t(`careers.jobs.${role.id}.about`)}</p>
      <h2>{t('careers.whatYouDo')}</h2>
      <ul>
        {duties.map((d, i) => (
          <li key={i}>{d}</li>
        ))}
      </ul>
      <h2>{t('careers.whatLooking')}</h2>
      <ul>
        {reqs.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
      <h2>{t('careers.niceToHave')}</h2>
      <ul>
        {nice.map((n, i) => (
          <li key={i}>{n}</li>
        ))}
      </ul>
    </article>
  )
}

function PerkCard({ perk }: { perk: (typeof PERKS)[number] }) {
  const t = useTranslations('company')
  return (
    <div className="perk">
      <span className="perk-ico">
        <PerkIconSvg icon={perk.icon} />
      </span>
      <h3>{t(`careers.${perk.titleKey}`)}</h3>
      <p>{t(`careers.${perk.descKey}`)}</p>
    </div>
  )
}

function PerkIconSvg({ icon }: { icon: PerkIcon }) {
  const common = {
    width: 20,
    height: 20,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    viewBox: '0 0 24 24'
  }
  if (icon === 'spark') {
    return (
      <svg {...common}>
        <path d="M12 3v5M12 16v5M5 12H3M21 12h-2M7.8 7.8 6.4 6.4M17.6 17.6l-1.4-1.4M7.8 16.2l-1.4 1.4M17.6 6.4l-1.4 1.4" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )
  }
  if (icon === 'globe') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20M2 12h20" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}
